import { h } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import { button, toast } from "@/components/ui";
import { notesService, topicsService, materialsService, subjectsService, pbErrorMessage } from "@/services";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { homeworkSnippet, nextLessonDate, syncHomeworkFromNote } from "@/features/homework/fromNotes";
import { renderMarkdown } from "@/markdown";
import { bindTaskCheckboxes } from "@/markdown/tasks";
import { hydrateDiagrams } from "@/markdown/diagrams";
import { renderPreview } from "@/markdown/preview";
import { fitTables } from "@/markdown/tableFit";
import { createPdfPanel, pdfPanelFits } from "@/features/editor/pdfPanel";
import { fileRef } from "@/markdown/fileref";
import { openCropDialog } from "@/features/materials/cropDialog";
import { openTableEditor, findTableAt, tsvToTable } from "@/features/editor/tableEditor";
import { attachMarkdownKeys } from "@/features/editor/keys";
import { createCodeEditor } from "@/features/editor/codeEditor";
import { openTreeFor } from "@/features/editor/treeInsert";
import { redo, undo } from "@codemirror/commands";
import { attachPreviewFold } from "@/features/editor/previewFold";
import { createSparklesBar } from "@/features/editor/sparkles";
import { attachSyncScroll, loadSyncLinked, saveSyncLinked } from "@/features/editor/syncScroll";
import { openDrawDialog } from "@/features/materials/drawDialog";
import {
  CONTAINER_MENU,
  SLASH_CMDS,
  SNIPPETS,
  attachSlashMenu,
  insertTermDef,
  pickImage,
  todayStr,
  type Cmd,
  type MenuEntry,
} from "@/features/editor/slash";
import {
  STRIP,
  formatStateAt,
  insertSnippet,
  insertText,
  keepSelection,
  toggleLinePrefix,
  toggleOrderedList,
  wrapSelection,
  type FormatState,
} from "@/features/editor/format";
import { navigate, type RouteContext } from "@/app/router";
import type { NoteKind } from "@/types/models";

/**
 * Eigener Markdown-Editor (keine Fremd-Lib): Textarea + Live-Vorschau,
 * Toolbar mit Container-Auswahl, Slash-Befehle (mit Enter/Klick-Bestätigung),
 * Tabellen, KaTeX, Undo/Redo (CodeMirror-Historie), Autosave.
 */

const KIND_META: Record<NoteKind, { label: string; icon: IconName }> = {
  mitschrift: { label: "Mitschrift", icon: "pencil" },
  lernzettel: { label: "Lernzettel", icon: "note" },
};

type Mode = "split" | "text" | "preview";
const MODES: { key: Mode; label: string }[] = [
  { key: "text", label: "Text" },
  { key: "split", label: "Geteilt" },
  { key: "preview", label: "Vorschau" },
];

export async function EditorPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const kind = ctx.params.kind as NoteKind;
  if (kind !== "mitschrift" && kind !== "lernzettel") {
    return h("div", { class: "card card-pad" }, "Unbekannter Notiztyp.");
  }
  const meta = KIND_META[kind];

  let noteId = "";
  let topicTitle = "";
  let initialValue = "";
  let referenceContent = ""; // jeweils andere Notizart (read-only Referenz)
  // Offline entstanden statt online geladen: Speichern geht dann nicht direkt
  // auf den Server, sondern in die Offline-Ablage (siehe doSave unten).
  let offline = false;
  let homeworkDueDate = new Date();
  let subjectId = "";
  const otherKind: NoteKind = kind === "lernzettel" ? "mitschrift" : "lernzettel";
  const fetchFresh = () =>
    Promise.all([
      topicsService.get(topicId).catch(() => null),
      notesService.getOrCreate(topicId, kind),
      notesService.listByTopic(topicId).catch(() => []),
    ]);
  const quick = navigator.onLine ? await offlineSync.loadBaseIfClean(topicId, kind).catch(() => null) : null;
  let freshInBackground: ReturnType<typeof fetchFresh> | null = null;
  if (quick) {
    const [cachedTopics, cachedOther] = await Promise.all([
      offlineSync.loadTopics().catch(() => null),
      offlineSync.loadNote(topicId, otherKind).catch(() => null),
    ]);
    const cachedTopic = cachedTopics?.find((t) => t.id === topicId);
    noteId = quick.noteId;
    initialValue = quick.content;
    topicTitle = cachedTopic?.title ?? "";
    subjectId = cachedTopic?.subject ?? "";
    referenceContent = cachedOther?.content ?? "";
    freshInBackground = fetchFresh();
  } else try {
    const [topic, doc, other] = await fetchFresh();
    topicTitle = topic?.title ?? "";
    noteId = doc.id;
    initialValue = doc.content ?? "";
    referenceContent = other.find((n) => n.kind === otherKind)?.content ?? "";
    subjectId = topic?.subject ?? "";
    void offlineSync.saveBase(topicId, kind, noteId, initialValue).catch(() => undefined);
  } catch (err) {
    // Netzwerk down: zuletzt online gecachten Stand versuchen (siehe
    // offline-sync.service.ts), bevor die Seite ganz aufgibt.
    const cached = await offlineSync.loadNote(topicId, kind).catch(() => null);
    if (!cached) {
      return h(
        "div",
        { class: "card card-pad" },
        h("p", { class: "field-error" }, pbErrorMessage(err)),
        button({ label: "Zurück zum Thema", icon: "chevron-left", onClick: () => navigate(`/topic/${topicId}`) })
      );
    }
    noteId = cached.noteId;
    initialValue = cached.content;
    offline = true;
  }

  const homeworkCmd: Cmd = { label: "Hausaufgabe", keywords: "hausaufgabe hw aufgabe frist", snippet: homeworkSnippet(homeworkDueDate) };
  if (subjectId) {
    void nextLessonDate(subjectId).then((date) => {
      homeworkDueDate = date;
      homeworkCmd.snippet = homeworkSnippet(date);
    }, () => undefined);
  }
  const cmds: Cmd[] = [
    homeworkCmd,
    // Nur im großen Editor sinnvoll (Sparkles gibt es hier, nicht im
    // Hausaufgaben-Dialog) – deshalb hier lokal statt in SNIPPETS/SLASH_CMDS.
    { label: "Sparkles (KI-Assistent)", keywords: "sparkles ki assistent ai schreiben helfer", snippet: "", action: "sparkles" },
    { label: "Skizze zeichnen", keywords: "skizze zeichnen zeichnung tafelbild canvas malen", snippet: "", action: "sketch" },
    ...SLASH_CMDS,
  ];

  // Quelltext-Seite: CodeMirror 6 – Mehrfachcursor (Alt/mittlere Maustaste
  // ziehen) und Falten wie in VS Code gibt es auf einem <textarea> nicht.
  const ta = createCodeEditor({
    value: initialValue,
    placeholder: "Schreibe in Markdown … Tippe „/“ für Befehle.",
    // `openTable` ist eine Funktionsdeklaration weiter unten (hochgezogen).
    onEditTable: () => openTable(),
    // `openTree` ist eine Funktionsdeklaration weiter unten (hochgezogen).
    onEditTree: () => openTree(),
    onOpenPdfRef: (href) => openPdfRef(href),
    onEditSketch: () => void insertDrawing(),
    // Zugeklappte Bereiche über Reload/Neuöffnen hinweg merken (pro Notiz).
    foldId: `note:${noteId}`,
  });

  const preview = h("div", { class: "editor-preview markdown-body" });

  /**
   * TOC-/Anker-Klicks innerhalb eines gerenderten Markdown-Containers abfangen:
   * im Container scrollen statt den Hash zu ändern (sonst navigiert der
   * Hash-Router auf eine nicht existierende Route -> 404).
   */
  const interceptAnchors = (root: HTMLElement) => {
    root.addEventListener("click", (e) => {
      const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
      if (!a || !root.contains(a)) return;
      const href = a.getAttribute("href") || "";
      if (href.length < 2) return;
      e.preventDefault();
      const id = decodeURIComponent(href.slice(1));
      const target = root.querySelector(
        "#" + (window.CSS && CSS.escape ? CSS.escape(id) : id)
      );
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  preview.addEventListener("click", (e) => {
    const link = (e.target as HTMLElement).closest<HTMLAnchorElement>("a.md-pdf-link");
    if (!link) return;
    e.preventDefault();
    openPdfRef(link.getAttribute("href") ?? "");
  });
  interceptAnchors(preview);

  // Referenz-Panel: zeigt read-only die jeweils andere Notizart (Mitschrift
  // beim Lernzettel und umgekehrt). Per Button ein-/ausblendbar.
  const referencePanel = h("div", { class: "editor-reference markdown-body" },
    h("div", { class: "editor-ref-head" },
      h("span", { class: "row", html: icon(KIND_META[otherKind].icon, 15) }),
      KIND_META[otherKind].label,
      h("span", { class: "muted text-sm" }, " (Referenz)")
    ),
    referenceContent
      ? h("div", { class: "editor-ref-body markdown-body", html: renderMarkdown(referenceContent) })
      : h("div", { class: "editor-ref-body muted text-sm" }, `Noch keine ${KIND_META[otherKind].label}.`)
  );
  interceptAnchors(referencePanel);
  fitTables([referencePanel]);

  const textWrap = h("div", { class: "editor-text-wrap" }, ta.dom);
  // Sparkles hängt HIER (nicht als Geschwister der ganzen Editor-Fläche),
  // damit die Leiste automatisch so breit ist wie die Quelltext-Spalte –
  // egal ob Split-, 3er- oder reine Textansicht.
  const textShell = h("div", { class: "editor-text-shell" }, textWrap);

  // Arbeits-PDF als zweite Spalte (nur breite Schirme, siehe pdfPanel.ts).
  const pdfPanel = createPdfPanel(topicId);

  // Kopplung Quelltext/Vorschau umschalten (unten in der geteilten Ansicht,
  // siehe .editor-sync-toggle). Standardmäßig getrennt, siehe syncScroll.ts;
  // Zustand pro Notiz gemerkt.
  const syncToggle = h("button", {
    type: "button",
    class: "editor-sync-toggle",
    title: "Vorschau und Quelltext verbinden",
    onclick: () => {
      const linked = !sync.isLinked();
      sync.setLinked(linked);
      saveSyncLinked(noteId, linked);
      syncToggle.classList.toggle("is-linked", linked);
      syncToggle.title = linked
        ? "Vorschau und Quelltext getrennt scrollen"
        : "Vorschau und Quelltext verbinden";
      syncToggle.innerHTML = icon(linked ? "link" : "link-off", 15);
    },
    html: icon("link-off", 15),
  });

  const area = h(
    "div",
    { class: "editor-area mode-split" },
    textShell,
    preview,
    referencePanel,
    pdfPanel.el,
    syncToggle
  );

  /**
   * Mit offener PDF-Spalte sind Ansichtsmodus und Referenz nicht mehr zu
   * haben – drei Arbeitsflächen nebeneinander kann niemand lesen. Die
   * Knöpfe werden deshalb sichtbar stillgelegt, statt wirkungslos dazustehen.
   */
  function lockLayoutControls(locked: boolean): void {
    const controls = [...Object.values(segButtons), titleMark, refWord];
    for (const el of controls) {
      el.dataset.title ??= el.title;
      el.toggleAttribute("disabled", locked);
      el.classList.toggle("is-locked", locked);
      el.title = locked ? "Erst das Arbeits-PDF schließen" : (el.dataset.title ?? "");
    }
  }

  /**
   * Beim Aufklappen des PDFs steht der Quelltext links und das Material
   * rechts – die Vorschau und die Referenz treten so lange zurück, drei
   * Arbeitsflächen nebeneinander kann niemand mehr lesen.
   */
  let pdfBtn: HTMLElement | null = null;
  let modeBeforePdf: "split" | "text" | "preview" = "split";
  async function togglePdf(): Promise<void> {
    const willOpen = !pdfPanel.isOpen();
    if (willOpen) {
      modeBeforePdf = area.classList.contains("mode-text")
        ? "text"
        : area.classList.contains("mode-preview")
          ? "preview"
          : "split";
      area.classList.remove("show-reference");
      setMode("text");
    }
    area.classList.toggle("show-pdf", willOpen);
    pdfBtn?.classList.toggle("active", willOpen);
    pdfRefBtn.style.display = willOpen ? "" : "none";
    lockLayoutControls(willOpen);
    const ok = await pdfPanel.toggle();
    if (!ok && willOpen) {
      toast("Arbeits-PDF ließ sich nicht öffnen.", "error");
      area.classList.remove("show-pdf");
      pdfBtn?.classList.remove("active");
      setMode(modeBeforePdf);
    }
    if (!willOpen) setMode(modeBeforePdf);
  }

  // Dreiecke am linken Rand der Vorschau; teilt sich den Faltzustand mit dem
  // Quelltext (siehe features/editor/previewFold.ts).
  const previewFold = attachPreviewFold(preview, ta);

  // Mitscrollen nur im geteilten Modus – sonst ist nur eine Seite sichtbar.
  // Standardmäßig getrennt (siehe syncScroll.ts), Zustand pro Notiz gemerkt.
  const sync = attachSyncScroll(ta, preview, () => area.classList.contains("mode-split"));
  if (loadSyncLinked(noteId)) {
    sync.setLinked(true);
    syncToggle.classList.add("is-linked");
    syncToggle.title = "Vorschau und Quelltext getrennt scrollen";
    syncToggle.innerHTML = icon("link", 15);
  }

  // Gerendert wird über den gemeinsamen Vorschau-Pfad (markdown/preview.ts) –
  // dieselbe Kette wie in den Hausaufgaben. Diagramme bleiben beim Tippen
  // verzögert, sonst hinge jeder Tastendruck an Mermaid/Kroki.
  const renderPreviewNow = () => {
    renderPreview(preview, ta.value, { diagramDelay: 450, onUpdated: () => sync.invalidate() });
    previewFold.apply();
  };
  // Die Vorschau tauscht nur noch geänderte Blöcke aus (markdown/reuse.ts),
  // ein Durchlauf ist damit billig. Die kurze Pause bündelt trotzdem schnelles
  // Tippen, damit nicht jeder einzelne Anschlag den Parser startet.
  let previewInputTimer: number | undefined;
  const renderPreviewDebounced = () => {
    window.clearTimeout(previewInputTimer);
    previewInputTimer = window.setTimeout(renderPreviewNow, 60);
  };
  // Referenz-Panel einmalig nachrendern (Diagramme dort ebenfalls anzeigen).
  void hydrateDiagrams(referencePanel);
  // Haken in der Vorschau schreiben direkt in den Quelltext zurück.
  bindTaskCheckboxes(
    preview,
    () => ta.value,
    (next) => {
      ta.value = next;
    }
  );

  // ---- Einfügen (alles über das Feld -> Undo/Redo bleibt erhalten) ----
  // Alle drei Helfer stellen die Auswahl danach wieder her, siehe
  // features/editor/format.ts.
  const insert = (text: string) => insertSnippet(ta, text);
  const wrap = (before: string, after: string, ph: string) =>
    wrapSelection(ta, before, after, ph);
  const linePrefix = (prefix: string, strip?: RegExp) =>
    toggleLinePrefix(ta, prefix, strip ? { strip } : {});
  const bulletList = () => toggleLinePrefix(ta, "- ", { strip: STRIP.bullet });
  const orderedList = () => toggleOrderedList(ta);
  /**
   * Ein Knopf statt zwei: aus -> Liste (Punkte) -> Aufzählung (nummeriert) ->
   * aus. `toggleLinePrefix`/`toggleOrderedList` ersetzen das jeweils andere
   * Präfix ohnehin schon mit (STRIP.bullet erkennt auch "1. "), ein einzelner
   * Aufruf genügt daher für jeden Schritt.
   */
  const cycleList = () => {
    const s = formatStateAt(ta);
    if (s.ordered) orderedList(); // schon nummeriert -> weg (toggleOrderedList entfernt dann)
    else if (s.bullet) orderedList(); // Punkte -> nummeriert
    else bulletList(); // nichts -> Punkte
  };

  // ---- Speicher-Anzeige ----
  const dot = h("span", { class: "save-dot saved" });
  const saveLabel = h("span", null, "Gespeichert");
  const saveIndicator = h("span", { class: "save-indicator" }, dot, saveLabel);
  const setSave = (s: "saved" | "saving" | "error", t: string) => {
    dot.className = "save-dot " + s;
    saveLabel.textContent = t;
  };

  // Speichern NICHT bei jedem Tastendruck, sondern wenn der Nutzer das Feld
  // verlässt, die Seite wechselt oder neu lädt – aber dann garantiert. `dirty`
  // markiert ungespeicherte Änderungen, `lastSaved` verhindert Doppel-Saves.
  let saving = false;
  let dirty = false;
  let lastSaved = initialValue;

  const markDirty = () => {
    if (ta.value === lastSaved) { dirty = false; setSave("saved", "Gespeichert"); return; }
    dirty = true;
    setSave("saving", "Nicht gespeichert");
  };

  async function doSave(): Promise<void> {
    if (saving || !dirty || ta.value === lastSaved) return;
    saving = true;
    const snapshot = ta.value;
    setSave("saving", "Speichern …");
    if (freshInBackground) await freshInBackground.catch(() => undefined);

    // Einmal offline gelandet, für den Rest der Sitzung nicht bei jeder
    // Änderung erneut einen Online-Versuch verlieren – direkt lokal ablegen.
    if (offline || !navigator.onLine) {
      await offlineSync.savePendingNote(topicId, kind, noteId, snapshot);
      lastSaved = snapshot;
      dirty = false;
      setSave("saved", "Offline gespeichert");
      saving = false;
      return;
    }

    try {
      await notesService.save(noteId, snapshot);
      lastSaved = snapshot;
      dirty = ta.value !== lastSaved;
      setSave(dirty ? "saving" : "saved", dirty ? "Nicht gespeichert" : "Gespeichert");
      void offlineSync.saveBase(topicId, kind, noteId, snapshot).catch(() => undefined);
      // Hausaufgaben-Blöcke abgleichen. Die Verknüpfung steht in der Datenbank
      // (`sourceNote`), der Text bleibt unberührt – bereinigt wird nur, was
      // eine frühere Fassung als `ID:`-Zeile hineingeschrieben hatte.
      const sync = await syncHomeworkFromNote(snapshot, topicId, noteId).catch(() => null);
      if (sync && sync.content !== snapshot && ta.value === snapshot) {
        lastSaved = sync.content;
        ta.value = sync.content;
        await notesService.save(noteId, sync.content).catch(() => {});
        void offlineSync.saveBase(topicId, kind, noteId, sync.content).catch(() => undefined);
        setSave("saved", "Gespeichert");
      }
      if (sync) {
        const n = (count: number, one: string, many: string) =>
          count === 1 ? one : `${count} ${many}`;
        const parts = [
          sync.created ? n(sync.created, "1 Hausaufgabe angelegt", "Hausaufgaben angelegt") : "",
          sync.updated ? n(sync.updated, "1 Hausaufgabe bearbeitet", "Hausaufgaben bearbeitet") : "",
          sync.removed ? n(sync.removed, "1 Hausaufgabe gelöscht", "Hausaufgaben gelöscht") : "",
        ].filter(Boolean);
        if (parts.length) toast(parts.join(" · "), "success");
      }
    } catch (err) {
      // Netzwerkfehler mitten in der Sitzung (WLAN weg): nicht einfach als
      // Fehler stehen lassen – lokal sichern, damit nichts verloren geht.
      if (!navigator.onLine) {
        offline = true;
        await offlineSync.savePendingNote(topicId, kind, noteId, snapshot);
        lastSaved = snapshot;
        dirty = false;
        setSave("saved", "Offline gespeichert");
      } else {
        setSave("error", "Nicht gespeichert");
        toast(pbErrorMessage(err), "error");
      }
    } finally {
      saving = false;
    }
  }

  void freshInBackground?.then(([topic, doc]) => {
    const fresh = doc.content ?? "";
    noteId = doc.id;
    if (topic?.title) topicTitle = topic.title;
    if (fresh === initialValue) return;
    if (!dirty && !saving && ta.value === initialValue) {
      ta.value = fresh;
      lastSaved = fresh;
      renderPreviewNow();
      void offlineSync.saveBase(topicId, kind, doc.id, fresh).catch(() => undefined);
      return;
    }
    // Schon getippt: nicht überschreiben, sondern wie offline sichern – der Sync führt zusammen.
    offline = true;
    toast("Auf dem Server liegt eine neuere Fassung – deine Änderungen werden beim Sync zusammengeführt.", "info");
  }, () => undefined);

  // Speichern bei: Verlassen des Textfelds, Tab-Wechsel, Seite-Schließen/Reload.
  ta.on("blur", () => {
    // Wechsel in die Sparkles-Leiste oder einen der eigenen Dialoge (Skizze,
    // Tabelle, PDF-Text, ...) ist kein "Feld verlassen" – der Speicherversuch
    // lief sonst bei jedem Klick dorthin an, und ein Reload/Verlassen kurz
    // danach traf `dirty` mitten im noch laufenden Save (native "Änderungen
    // gehen verloren"-Warnung, obwohl längst gespeichert wurde). Andere
    // Fokuswechsel (z. B. wirklich eine andere Seite/App) lösen weiter sofort aus.
    const active = document.activeElement;
    if (sparkles.el.contains(active)) return;
    if (document.querySelector(".modal-backdrop")?.contains(active)) return;
    void doSave();
  });
  const onVisibility = () => { if (document.visibilityState === "hidden") void doSave(); };
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!dirty) return;
    // Synchroner Save-Versuch + Browserwarnung als Fallback.
    void doSave();
    e.preventDefault();
    e.returnValue = "";
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("beforeunload", onBeforeUnload);
  /**
   * Aufräumen, wenn der Editor aus dem DOM verschwindet (Navigation im SPA).
   * Wird ASYNC zurückgegeben und an jeder Aufrufstelle abgewartet – vorher
   * lief `pdfPanel.close()` (speichert Textbox-Änderungen im Arbeits-PDF)
   * unbeobachtet im Hintergrund weiter, während die Seite schon wechselte.
   * Schlug der Speicherversuch dann fehl (z. B. abgelaufene Sitzung), lief
   * die Fehlermeldung ins Leere – der Nutzer war ja schon auf der nächsten
   * Seite. Jetzt wartet die Navigation darauf, bevor sie weitergeht.
   */
  const cleanup = async (): Promise<void> => {
    window.clearTimeout(previewInputTimer);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("beforeunload", onBeforeUnload);
    rememberScroll();
    previewFold.destroy();
    sync.destroy();
    document.removeEventListener("click", closeContainerMenu);
    await Promise.all([doSave(), pdfPanel.close()]);
  };

  /**
   * Verweis auf die gerade sichtbare PDF-Seite einfügen. Gespeichert wird
   * Datei + Seite **innerhalb** dieser Datei, nicht die Seite im
   * zusammengefügten Dokument – so zeigt der Verweis auch dann noch richtig,
   * wenn später Dateien dazukommen oder umsortiert werden.
   */
  async function insertPdfRef(): Promise<void> {
    const ref = pdfPanel.currentRef();
    if (!ref) {
      toast("Öffne zuerst das Arbeits-PDF an der gewünschten Seite.", "info");
      return;
    }
    // Die ersten Worte des Seitentextes statt des Dateinamens – erkennbarer,
    // welche Stelle gemeint ist. Kein Textlayer (Scan), Ladefehler oder
    // langsamer als ein Augenblick: Materialname als Rückfall.
    const heading = await Promise.race([
      pdfPanel.currentHeading().catch(() => ""),
      new Promise<string>((resolve) => window.setTimeout(() => resolve(""), 700)),
    ]);
    const label = heading || ref.label;
    insertSnippet(ta, `[📄 ${label}, S. ${ref.page}](pdf:${ref.material}:${ref.page})`);
    ta.emitInput();
  }

  /** Klick auf einen solchen Verweis – in der Vorschau wie im Quelltext. */
  function openPdfRef(href: string): void {
    const match = /^pdf:([^:]+):(\d+)$/.exec(href);
    if (!match) return;
    const ref = { material: match[1], page: Number(match[2]) };
    if (!pdfPanel.isOpen()) area.classList.add("show-pdf");
    void pdfPanel.showRef(ref).then((ok) => {
      if (ok) {
        lockLayoutControls(true);
        pdfRefBtn.style.display = "";
        pdfBtn?.classList.add("active");
        setMode("text");
        area.classList.add("show-pdf");
      } else {
        area.classList.remove("show-pdf");
        pdfRefBtn.style.display = "none";
        toast("Diese Seite gibt es im Arbeits-PDF nicht mehr.", "error");
      }
    });
  }

  /** Baumdiagramm: das am Cursor bearbeiten oder ein neues einfügen. */
  function openTree() {
    openTreeFor(ta);
  }

  /** Visueller Tabellen-Editor: bestehende Tabelle am Cursor oder neue. */
  function openTable() {
    const block = findTableAt(ta.value, ta.selectionStart);
    openTableEditor({
      initial: block?.cells,
      meta: block?.meta,
      onSave: (md) => {
        ta.focus();
        if (block) {
          ta.setSelectionRange(block.start, block.endWithFormulas);
          ta.insert(md);
        } else {
          ta.insert(`\n\n${md}\n\n`);
        }
      },
    });
  }
  /**
   * Lädt ein Bild hoch und fügt es als Markdown-Bild ein. Im Quelltext steht
   * nur die Kurzform `pb:<id>/<datei>` statt der kompletten PocketBase-
   * Adresse – aufgelöst wird beim Rendern (markdown/fileref.ts).
   */
  function uploadPhoto(file: File): void {
    toast("Lade Foto hoch …");
    void materialsService
      .upload(topicId, file)
      .then((m) => {
        insertText(ta, `\n![${m.title}](${fileRef(m.id, m.file)})\n`);
        void openCropDialog({
          imageUrl: materialsService.urlFor(m),
          mimeType: m.type,
          backupKey: m.id,
          onSave: async (blob) => {
            // PocketBase vergibt beim Schreiben eines Datei-Feldes IMMER einen
            // neuen Dateinamen, auch bei diesem Material (gleiche ID). Die
            // Kurzform im Quelltext trägt noch den alten – ohne diese
            // Korrektur zeigt die Vorschau danach "nicht gefunden".
            const oldRef = fileRef(m.id, m.file);
            const updated = await materialsService.saveDetails(m.id, { title: m.title, file: blob, fileName: m.file });
            const newRef = fileRef(updated.id, updated.file);
            if (newRef !== oldRef) ta.value = ta.value.split(oldRef).join(newRef);
            renderPreviewNow();
          },
        });
      })
      .catch((err) => toast(pbErrorMessage(err), "error"));
  }
  /** Slash-Befehl „/foto": Dateidialog (Kamera oder Mediathek). */
  function insertPhoto(): void {
    pickImage(uploadPhoto);
  }

  /**
   * Steht der Cursor auf einer Zeile, die NUR ein Bild ist und auf eine
   * `.svg`-Datei zeigt ("![Alt](pb:<id>/<datei>.svg)"), liefert das die
   * Material-ID – für "/skizze": vorhandene Skizze weiterbearbeiten statt
   * eine neue anzulegen. Gleiches Prinzip wie `findTableAt` in
   * tableEditor.ts.
   */
  function svgRefAtCursor(text: string, pos: number): string | null {
    const lines = text.split("\n");
    let acc = 0;
    for (const line of lines) {
      const lineEnd = acc + line.length;
      if (pos >= acc && pos <= lineEnd) {
        const m = /^!\[[^\]]*\]\(pb:([^/]+)\/[^)]+\.svg\)$/i.exec(line.trim());
        return m ? m[1] : null;
      }
      acc = lineEnd + 1;
    }
    return null;
  }

  /**
   * Slash-Befehl „/skizze": Zeichenfeld als Popup (z. B. ein Tafelbild in
   * Physik). Gezeichnet wird NUR im Popup – im Quelltext landet danach ein
   * ganz normales Bild, kein Canvas/keine Vektordaten (siehe drawDialog.ts).
   * Steht der Cursor auf einer vorhandenen Skizze, wird DIESE bearbeitet.
   */
  async function insertDrawing(): Promise<void> {
    const materialId = svgRefAtCursor(ta.value, ta.selectionStart);
    if (materialId) {
      try {
        const material = await materialsService.get(materialId);
        const initialSvg = await fetch(materialsService.urlFor(material)).then((r) => r.text());
        openDrawDialog({
          initialSvg,
          onSave: (blob) => {
            toast("Speichere Skizze …");
            const oldRef = fileRef(material.id, material.file);
            void materialsService
              .saveDetails(material.id, { title: material.title, file: blob, fileName: material.file })
              .then((updated) => {
                const newRef = fileRef(updated.id, updated.file);
                if (newRef !== oldRef) ta.value = ta.value.split(oldRef).join(newRef);
                renderPreviewNow();
                toast("Skizze aktualisiert.", "success");
              })
              .catch((err) => toast(pbErrorMessage(err), "error"));
          },
        });
      } catch (err) {
        toast(pbErrorMessage(err), "error");
      }
      return;
    }

    openDrawDialog({
      onSave: (blob) => {
        toast("Lade Skizze hoch …");
        void materialsService
          .uploadBlob(topicId, blob, "skizze.svg", "Skizze")
          .then((m) => insertText(ta, `\n![${m.title}](${fileRef(m.id, m.file)})\n`))
          .catch((err) => toast(pbErrorMessage(err), "error"));
      },
    });
  }

  // Strg+V mit einem Bild in der Zwischenablage lädt direkt hoch – genau wie
  // in Slack/Discord üblich, kein Umweg über den Dateidialog.
  ta.on("paste", (e: ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const imageItem = items.find((it) => it.kind === "file" && it.type.startsWith("image/"));
    if (!imageItem) return; // normaler Text-Paste bleibt unangetastet
    const file = imageItem.getAsFile();
    if (!file) return;
    e.preventDefault();
    uploadPhoto(file);
  });

  // Zellen aus Excel/Numbers/Google Tabellen: als Markdown-Tabelle einfügen.
  ta.on("paste", (e: ClipboardEvent) => {
    const table = tsvToTable(e.clipboardData?.getData("text/plain") ?? "");
    if (!table) return;
    e.preventDefault();
    const before = ta.value.slice(0, ta.selectionStart);
    const lead = !before || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
    ta.insert(`${lead}${table}\n\n`);
  });

  // URL einfügen, während Text markiert ist: der markierte Text wird zum
  // Linktext statt einfach durch die rohe Adresse ersetzt zu werden.
  ta.on("paste", (e: ClipboardEvent) => {
    const url = (e.clipboardData?.getData("text/plain") ?? "").trim();
    if (!/^https?:\/\/\S+$/.test(url)) return;
    const selStart = ta.selectionStart;
    const selEnd = ta.selectionEnd;
    if (selEnd <= selStart) return;
    const text = ta.value.slice(selStart, selEnd);
    e.preventDefault();
    ta.setSelectionRange(selStart, selEnd);
    ta.insert(`[${text}](${url})`);
    ta.emitInput();
  });

  // Slash-Menü vor den eigenen Tastatur-Handlern anhängen: solange es offen
  // ist, verbraucht es Pfeiltasten/Enter selbst.
  const slashMenu = attachSlashMenu(ta, {
    cmds,
    onAction: (c) => {
      if (c.action === "table") return openTable();
      if (c.action === "pdfpage") return void insertPdfRef();
      if (c.action === "sparkles") return sparkles.open();
      if (c.action === "sketch") return void insertDrawing();
      if (c.action === "term") return insertTermDef(ta);
      if (c.action === "tree") return openTree();
      insertPhoto();
    },
  });

  ta.on("input", () => {
    renderPreviewDebounced();
    markDirty();
    sync.followCaret();
  });

  // Tab/Enter (Einrücken, Listen, Tabellen) – geteilt mit dem Hausaufgaben-Feld.
  attachMarkdownKeys(ta);

  ta.on("keydown", (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();
      if (k === "b") {
        e.preventDefault();
        wrap("**", "**", "fett");
      } else if (k === "i") {
        e.preventDefault();
        wrap("*", "*", "kursiv");
      } else if (k === "s") {
        e.preventDefault();
        dirty = ta.value !== lastSaved;
        void doSave();
      }
      // Strg+Z / Strg+Y: übernimmt die Undo-Historie von CodeMirror.
    }
  });

  // ---- Container-Dropdown ----
  const containerMenu = h("div", { class: "tool-dropdown", style: "display:none" });

  /**
   * Baut das Container-Menü. Einträge mit `children` (z. B. „Box") öffnen ihr
   * Untermenü als Flyout rechts daneben – schon bei Hover, kein Klick nötig.
   */
  const buildMenu = (entries: MenuEntry[], into: HTMLElement, depth = 0) => {
    for (const entry of entries) {
      if (entry.children) {
        const group = h("div", { class: "tool-dropdown-group" });
        const sub = h("div", { class: "tool-dropdown-sub" });
        const head = keepSelection(
          h(
            "button",
            {
              type: "button",
              class: "tool-dropdown-item has-sub",
              // Kein Hover am iPad: Antippen klappt das Untermenü auf/zu.
              onclick: (e: MouseEvent) => {
                e.stopPropagation();
                group.classList.toggle("is-open");
              },
            },
            entry.label
          )
        );
        buildMenu(entry.children, sub, depth + 1);
        group.append(head, sub);
        into.appendChild(group);
        continue;
      }
      into.appendChild(
        keepSelection(
          h(
            "button",
            {
              type: "button",
              class: "tool-dropdown-item",
              onclick: () => {
                if (entry.action === "term") insertTermDef(ta);
                else if (entry.action === "tree") openTree();
                else insert(entry.snippet ?? "");
                containerMenu.style.display = "none";
              },
            },
            entry.label
          )
        )
      );
    }
  };
  buildMenu(CONTAINER_MENU, containerMenu);
  const containerBtn = keepSelection(
    h(
      "button",
      {
        type: "button",
        class: "tool-btn",
        title: "Container einfügen (Info, Aufklappbar, KaTeX, TikZ …)",
        onclick: (e: MouseEvent) => {
          e.stopPropagation();
          const open = containerMenu.style.display === "none";
          containerMenu.style.display = open ? "block" : "none";
          if (open) placeContainerMenu();
        },
      },
      "Container ▾"
    )
  );
  const closeContainerMenu = () => {
    containerMenu.style.display = "none";
  };
  /** Unter dem Knopf, aber immer im Bildschirm (siehe `.tool-dropdown` in v1.css). */
  function placeContainerMenu(): void {
    const r = containerBtn.getBoundingClientRect();
    const w = Math.max(containerMenu.offsetWidth, 200);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    containerMenu.style.left = `${left}px`;
    containerMenu.style.top = `${r.bottom + 4}px`;
    // Das Flyout („Box") ist ~180 px breit – passt es rechts nicht mehr hin, nach links.
    containerMenu.classList.toggle("flip-sub", left + w + 180 > window.innerWidth);
  }
  document.addEventListener("click", closeContainerMenu);
  const containerWrap = h("span", { class: "tool-dropdown-wrap" }, containerBtn, containerMenu);

  // Sparkles-Leiste am unteren Rand – der Editor bleibt daneben bedienbar
  // (scrollen, markieren), deshalb eine Leiste statt eines Dialogs.
  const sparkles = createSparklesBar({
    ta,
    topicId,
    topicTitle,
    otherLabel: KIND_META[otherKind].label,
    otherContent: referenceContent,
  });
  textShell.appendChild(sparkles.el);

  // ---- Toolbar ----
  // `active` meldet, ob die Auszeichnung an der Cursorposition gerade gilt –
  // der Knopf wird dann hervorgehoben (siehe syncToolbar).
  const stateButtons: { el: HTMLElement; active: (s: FormatState) => boolean }[] = [];
  const register = (el: HTMLElement, active?: (s: FormatState) => boolean) => {
    keepSelection(el);
    if (active) stateButtons.push({ el, active });
    return el;
  };
  const tb = (
    label: string,
    title: string,
    run: () => void,
    cls = "",
    active?: (s: FormatState) => boolean
  ) =>
    register(
      h("button", { type: "button", class: "tool-btn " + cls, title, onclick: run }, label),
      active
    );
  const tbIcon = (
    name: IconName,
    title: string,
    run: () => void,
    active?: (s: FormatState) => boolean
  ) =>
    register(
      h("button", {
        type: "button",
        class: "tool-btn tool-icon",
        title,
        html: icon(name, 16),
        onclick: run,
      }),
      active
    );
  const sep = () => h("span", { class: "tool-sep" });

  // Ein Knopf für beide Listenarten (siehe cycleList oben): Symbol und Titel
  // zeigen, wohin ein Klick als Nächstes wechselt, nicht den aktuellen Stand.
  const listBtn = register(
    h("button", {
      type: "button",
      class: "tool-btn tool-icon",
      title: "Liste",
      html: icon("list", 16),
      onclick: cycleList,
    }),
    (s) => s.bullet || s.ordered
  );

  // Ein Knopf für H1–H3 statt drei nebeneinander: die Beschriftung zeigt die
  // Ebene, auf der die Zeile GERADE steht (leer = keine Überschrift), man
  // sieht den Stand also direkt am Knopf statt an drei separaten Zuständen.
  const cycleHeading = () => {
    const s = formatStateAt(ta);
    const next = s.heading === 1 ? 2 : s.heading === 2 ? 3 : s.heading === 3 ? 0 : 1;
    if (next === 0) toggleLinePrefix(ta, "", { strip: STRIP.heading });
    else linePrefix([SNIPPETS.h1, SNIPPETS.h2, SNIPPETS.h3][next - 1], STRIP.heading);
  };
  const headingBtn = register(
    h("button", { type: "button", class: "tool-btn", title: "Überschrift", onclick: cycleHeading }, "H"),
    (s) => Boolean(s.heading)
  );

  let syncPending = false;
  const syncToolbar = () => {
    if (syncPending) return;
    syncPending = true;
    requestAnimationFrame(() => {
      syncPending = false;
      const state = formatStateAt(ta);
      for (const b of stateButtons) b.el.classList.toggle("is-active", b.active(state));
      listBtn.innerHTML = icon(state.ordered ? "list" : state.bullet ? "list-ordered" : "list", 16);
      listBtn.title = state.ordered ? "Aufzählung entfernen" : state.bullet ? "Zu Aufzählung wechseln" : "Liste";
      headingBtn.textContent = state.heading ? `H${state.heading}` : "H";
      headingBtn.title = state.heading === 3 ? "Überschrift entfernen" : `Überschrift ${state.heading ? state.heading + 1 : 1}`;
    });
  };
  ta.on("input", syncToolbar);
  ta.on("select", syncToolbar);

  /** Zur jeweils anderen Notizart wechseln (speichert vorher). */
  const gotoOther = () => {
    dirty = ta.value !== lastSaved;
    void cleanup().then(() => {
      navigate(`/topic/${topicId}/edit/${otherKind}`);
    });
  };

  // Zwei klar getrennte Bedienelemente – beide tun immer dasselbe:
  //   Wort („Mitschrift"/„Lernzettel")  -> wechselt zur anderen Notizart.
  //   Symbol daneben (Seitenleiste)     -> klappt die Referenz auf/zu.
  const toggleReference = () => {
    const on = area.classList.toggle("show-reference");
    titleMark.classList.toggle("is-linked", on);
    titleMark.title = on
      ? `${KIND_META[otherKind].label} ausblenden`
      : `${KIND_META[otherKind].label} als Referenz einblenden`;
    if (on) void hydrateDiagrams(referencePanel);
  };

  const refWord = h("button", {
    type: "button",
    class: "tool-btn",
    title: `Zu ${KIND_META[otherKind].label} wechseln`,
    onmousedown: (e: MouseEvent) => e.preventDefault(),
    onclick: gotoOther,
  }, KIND_META[otherKind].label);

  const titleMark = h("button", {
    type: "button",
    class: "e-title-mark",
    title: `${KIND_META[otherKind].label} als Referenz einblenden`,
    onmousedown: (e: MouseEvent) => e.preventDefault(),
    onclick: (e: MouseEvent) => {
      e.stopPropagation();
      toggleReference();
    },
    html: icon("sidebar", 16),
  });

  // Der Knopf für das Arbeits-PDF sitzt direkt neben dem Umschalter für den
  // jeweils anderen Text – beides klappt eine zweite Spalte auf.
  const pdfMark = h("button", {
    type: "button",
    class: "e-title-mark",
    title: "Arbeits-PDF daneben öffnen",
    onmousedown: (e: MouseEvent) => e.preventDefault(),
    onclick: (e: MouseEvent) => {
      e.stopPropagation();
      void togglePdf();
    },
    html: icon("file", 16),
  });
  pdfBtn = pdfMark;

  const refGroup = h(
    "span",
    { class: "tool-ref-group" },
    refWord,
    titleMark,
    pdfPanelFits() ? pdfMark : null
  );

  // Der Knopf ergibt nur bei offener PDF-Spalte Sinn – sonst weiß niemand,
  // auf welche Seite er zeigen soll.
  const pdfRefBtn = tbIcon("file", "Verweis auf die aktuelle Seite im Arbeits-PDF", () =>
    void insertPdfRef()
  );
  pdfRefBtn.style.display = "none";

  const toolbar = h(
    "div",
    { class: "editor-toolbar" },
    tbIcon("undo", "Rückgängig (Strg+Z)", () => { ta.focus(); undo(ta.view); }),
    tbIcon("redo", "Wiederholen (Strg+Y)", () => { ta.focus(); redo(ta.view); }),
    sep(),
    headingBtn,
    listBtn,
    sep(),
    tb("B", "Fett (Strg+B)", () => wrap("**", "**", "fett"), "tb-bold", (s) => s.bold),
    tb("I", "Kursiv (Strg+I)", () => wrap("*", "*", "kursiv"), "tb-italic", (s) => s.italic),
    tbIcon("check-square", "Abhakbare Aufgabe", () => linePrefix(SNIPPETS.aufgabe, STRIP.task), (s) => s.task),
    tbIcon("quote", 'Zitat (Autor optional: "::: zitat Name")', () => insert(SNIPPETS.zitat)),
    tbIcon("code", "Code", () => wrap("`", "`", "code"), (s) => s.code),
    tbIcon("link", "Link", () => wrap("[", "](https://)", "Text")),
    tbIcon("table", "Visueller Tabellen-Editor (bearbeitet auch die Tabelle am Cursor)", openTable),
    pdfRefBtn,
    tbIcon("camera", "Foto aufnehmen oder hochladen", insertPhoto),
    tbIcon("pencil", "Skizze zeichnen (Tafelbild)", () => void insertDrawing()),
    tbIcon("sparkles", "Sparkles – KI fügt auf Zuruf Inhalt ein", () => sparkles.toggle()),
    sep(),
    tbIcon("pin", "Heutiges Datum als Überschrift", () => insert(`## ${todayStr()}\n`)),
    tb("Hausaufgabe", "Hausaufgabe notieren – wird beim Speichern übernommen", () => insert(homeworkSnippet(homeworkDueDate))),
    tb("Inhalt", "Inhaltsverzeichnis", () => insert(SNIPPETS.toc)),
    containerWrap
  );

  toolbar.addEventListener("scroll", closeContainerMenu, { passive: true });

  // ---- Modus-Umschalter ----
  const segButtons = {} as Record<Mode, HTMLButtonElement>;
  const segmented = h(
    "div",
    { class: "segmented" },
    ...MODES.map((m) => {
      const b = h("button", { type: "button", onclick: () => setMode(m.key) }, m.label);
      segButtons[m.key] = b;
      return b;
    })
  );
  const setMode = (mode: Mode) => {
    (Object.keys(segButtons) as Mode[]).forEach((k) =>
      segButtons[k].classList.toggle("active", k === mode)
    );
    // Nur die Moduskla­sse tauschen: Referenz- und PDF-Spalte sollen einen
    // Moduswechsel überleben.
    area.classList.remove("mode-split", "mode-text", "mode-preview");
    area.classList.add("mode-" + mode);
    toolbar.style.display = mode === "preview" ? "none" : "";
    if (mode !== "text") renderPreviewNow();
    requestAnimationFrame(() => ta.view.requestMeasure());
  };

  /** Eigener PDF-Export (kein Browser-Druck) – pdf-lib/html2canvas laden erst hier. */
  async function exportPdf(): Promise<void> {
    toast("PDF wird erstellt …");
    try {
      const [{ exportNotePdf }, topic] = await Promise.all([
        import("@/services/document/notePdfExport"),
        topicsService.get(topicId).catch(() => null),
      ]);
      const subject = topic ? await subjectsService.get(topic.subject).catch(() => undefined) : undefined;
      await exportNotePdf(ta.value, {
        topicTitle: topicTitle || "Notiz",
        subjectTitle: subject?.title,
        kindLabel: meta.label,
      });
      toast("PDF gespeichert.", "success");
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  const bar = h(
    "div",
    { class: "editor-bar" },
    button({
      icon: "chevron-left",
      variant: "ghost",
      title: "Zurück",
      onClick: async () => {
        dirty = ta.value !== lastSaved;
        await cleanup();
        navigate(`/topic/${topicId}`);
      },
    }),
    h(
      "span",
      { class: "e-title" },
      h("span", { class: "row", html: icon(meta.icon, 18) }),
      h("span", null, meta.label),
      topicTitle ? h("span", { class: "muted" }, ` · ${topicTitle}`) : null
    ),
    h("span", { class: "spacer" }),
    // Mitschrift/Lernzettel-Wechsel, Referenz und Arbeits-PDF stehen in der
    // Titelzeile: die Werkzeugleiste scrollt bis iPad-Breite horizontal, und
    // am Ende gelegene Knöpfe lagen dort außerhalb des sichtbaren Bereichs.
    refGroup,
    segmented,
    button({
      icon: "printer",
      variant: "ghost",
      title: "Als PDF exportieren",
      onClick: () => void exportPdf(),
    }),
    saveIndicator
  );

  // Scrollstand je Notiz merken – als ZEILE, nicht als Pixelwert: der Editor
  // vermisst sich nach dem Einhängen neu, wobei ein von Hand gesetzter
  // Pixelwert wieder verloren geht. Über die Zeile geht das Wiederherstellen
  // durch CodeMirrors eigenen Weg und hält.
  const scrollKey = `note_line_${topicId}_${kind}`;
  const rememberScroll = () => {
    try {
      localStorage.setItem(scrollKey, String(ta.topLine()));
    } catch {
      /* ignore */
    }
  };
  let scrollTimer: number | undefined;
  ta.on("scroll", () => {
    window.clearTimeout(scrollTimer);
    scrollTimer = window.setTimeout(rememberScroll, 200);
  });
  ta.on("blur", rememberScroll);

  renderPreviewNow();
  setMode("split");

  const savedLine = Number(localStorage.getItem(scrollKey));
  if (Number.isFinite(savedLine) && savedLine > 0) {
    // Zweimal: einmal sobald der Editor steht, und noch einmal, wenn die
    // Vorschau ihre Bilder und Diagramme fertig hat.
    const restore = () => {
      ta.scrollToLine(savedLine);
    };
    window.setTimeout(restore, 50);
    window.setTimeout(restore, 500);
  }

  const head = h("div", { class: "editor-head" }, bar, toolbar);
  const page = h("div", { class: "editor-page" }, head, area, slashMenu);

  // SPA-Navigation (Navbar/Hash) erkennen: sobald die Editor-Seite aus dem DOM
  // entfernt wird, speichern + Listener aufräumen. So geht beim Wegklicken
  // ohne Zurück-Button nichts verloren.
  const observer = new MutationObserver(() => {
    if (!document.body.contains(page)) {
      observer.disconnect();
      // Navigation ist an dieser Stelle schon passiert (Seite bereits aus dem
      // DOM) – hier lässt sich nichts mehr aufhalten, der Speicherversuch
      // läuft trotzdem zuverlässig zu Ende (Promises laufen unabhängig vom
      // Warten des Aufrufers weiter).
      void cleanup();
      // Erst jetzt abbauen: würde der Editor schon beim Klick auf „Zurück"
      // zerstört, stünde die Seite bis zum Seitenwechsel sichtbar leer da.
      ta.destroy();
    }
  });
  // Erst beobachten, wenn die Seite eingehängt ist.
  requestAnimationFrame(() => {
    if (page.parentNode) observer.observe(page.parentNode, { childList: true });
  });

  return page;
}
